import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import './Contact.css';

// Initialize EmailJS (as fallback)
emailjs.init('4yvEe0KZxW7SyExzh');

// Local CRA proxy uses /api; production sends to the live API
const API_URL = process.env.REACT_APP_API_URL || (
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
    ? ''
    : 'https://server-weld-ten-59.vercel.app'
);

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      showNotification('Please fill in all fields', 'error');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showNotification('Please enter a valid email address', 'error');
      return false;
    }

    return true;
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    let emailSent = false;

    try {
      // Try backend API first (Nodemailer/Gmail)
      try {
        console.log('Attempting to send via backend API (Nodemailer)...');
        console.log('API URL:', `${API_URL}/api/contact`);
        const backendResponse = await axios.post(`${API_URL}/api/contact`, {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        });

        console.log('Backend API response:', backendResponse.data);
        
        if (backendResponse.data.success) {
          emailSent = true;
          console.log('✅ Email sent successfully via Backend API');
        }
      } catch (backendError) {
        console.error('❌ Backend API Error Details:');
        console.error('Error Response:', backendError.response?.data);
        console.error('Error Status:', backendError.response?.status);
        console.error('Error Message:', backendError.message);
        console.error('Full Error:', backendError);
        console.warn('Backend API failed, trying EmailJS fallback...');
        // Continue to EmailJS fallback
      }

      // Fallback to EmailJS if backend failed
      if (!emailSent) {
        console.log('Sending email via EmailJS...');
        const templateParams = {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_email: 'hedroj@topmostsystem.com',
          reply_to: formData.email
        };

        const emailjsResponse = await emailjs.send(
          'service_m4g96fp',
          'template_g89tvg4',
          templateParams,
          '4yvEe0KZxW7SyExzh'
        );

        console.log('EmailJS response:', emailjsResponse);
        
        if (emailjsResponse && emailjsResponse.status === 200) {
          emailSent = true;
          console.log('✅ Email sent successfully via EmailJS');
        } else {
          throw new Error('Unexpected EmailJS response format');
        }
      }

      // Show success message
      if (emailSent) {
        showNotification('Thank you! Your message has been sent successfully.', 'success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      let errorMessage = 'Failed to send message. Please try again later.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = `Failed to send: ${error.message}`;
      } else if (error.text) {
        errorMessage = `Failed to send: ${error.text}`;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'fas fa-envelope',
      title: 'Email',
      info: 'hedroj@topmostsystem.com'
    },
    {
      icon: 'fas fa-phone',
      title: 'Phone',
      info: '+1 (514) 557 2640'
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Location',
      info: 'Montreal, Canada'
    }
  ];

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Get In Touch</h2>
          <p>Tell us about your website, app, or existing system — we will follow up with a clear next step.</p>
        </motion.div>

        <motion.div 
          className="contact-content"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                className="contact-item"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.4 + index * 0.1,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              >
                <i className={item.icon}></i>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.info}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.form 
            className="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="form-group">
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="Project Type"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <textarea
                id="message"
                name="message"
                placeholder="Tell us about your project..."
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>
            <motion.button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </motion.button>
          </motion.form>
        </motion.div>
      </div>

      {/* Notification */}
      {notification && (
        <motion.div
          className={`notification notification-${notification.type}`}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          transition={{ duration: 0.3 }}
        >
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              &times;
            </button>
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default Contact;


